// Where a per-device preference lives until there is an account to hold it.
// Today that is one thing: which of packages/core's ten accent hues this
// device is using (BRIEF "Answers, 2026-09-27" — Breeze carries no brand of
// its own, so the one look-related choice left is the accent). Plain
// localStorage now; the shape is a single get/set pair so moving this to a
// PocketBase `users.settings` field later touches only this file, not every
// call site.

import { DEFAULT_SETTINGS, isAccentHue, type AccentHue } from "@breeze/core";

const ACCENT_KEY = "breeze:accent";

export function getStoredAccent(): AccentHue {
  try {
    const raw = window.localStorage.getItem(ACCENT_KEY);
    if (raw && isAccentHue(raw)) return raw;
  } catch {
    // localStorage can throw in a private/blocked context. The default hue
    // is a fine fallback; nothing here is worth breaking the app over.
  }
  return DEFAULT_SETTINGS.theme.accent;
}

/** Whether this device picked an accent. Without one the page wears the house
 * accent the Worker put in it (worker/src/look.ts, QUEUE row 433). */
export function hasStoredAccent(): boolean {
  try {
    const raw = window.localStorage.getItem(ACCENT_KEY);
    return !!raw && isAccentHue(raw);
  } catch {
    return false;
  }
}

/** Back to the house accent. */
export function clearStoredAccent(): void {
  try {
    window.localStorage.removeItem(ACCENT_KEY);
  } catch {
    // Nothing was remembered.
  }
}

/** Whether the page carries a house accent at all. */
export function hasHouseAccent(): boolean {
  return typeof document !== "undefined" && !!document.querySelector('meta[name="breeze-house"]');
}

export function setStoredAccent(hue: AccentHue): void {
  try {
    window.localStorage.setItem(ACCENT_KEY, hue);
  } catch {
    // Same as above: losing the persisted preference for a session is not
    // worth surfacing an error over.
  }
}
